import { getApiUrl } from '~/config';
import {
  type ArcadeConfigData,
  type DailyGameStatus,
  type GameSessionData,
  type GuessData
} from '~/types';
import { getOrCreateAnonymousId } from '~/utils/anonymous';
import { throwApiError } from '~/utils/apiError';

/**
 * Complete the given game session.
 *
 * @param gameSessionId - Game session identifier to be completed
 *
 * @throws Error on API request failure.
 */
export const completeGameSession = async (
  gameSessionId: string
): Promise<GuessData> => {
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/sessions/${gameSessionId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Game-Session-ID': gameSessionId,
      'X-Anonymous-ID': getOrCreateAnonymousId()
    },
    credentials: 'include'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const fetchDailyGameStatus = async (): Promise<DailyGameStatus> => {
  const apiUrl = getApiUrl();

  const response = await fetch(`${apiUrl}/sessions/daily`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Anonymous-ID': getOrCreateAnonymousId()
    },
    credentials: 'include'
  });
  if (!response.ok) throw new Error(`Failed to get daily game status`);
  return response.json();
};

export const openDailyGameSession = async (): Promise<GameSessionData> => {
  const apiUrl = getApiUrl();

  const response = await fetch(`${apiUrl}/sessions/daily`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Anonymous-ID': getOrCreateAnonymousId()
    },
    credentials: 'include'
  });
  if (!response.ok) throw new Error(`Failed to open daily game session`);
  return response.json();
};

export const openLateDailyGameSession = async (
  date: string
): Promise<GameSessionData> => {
  const apiUrl = getApiUrl();

  const response = await fetch(`${apiUrl}/sessions/daily/${date}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Anonymous-ID': getOrCreateAnonymousId()
    },
    credentials: 'include'
  });

  if (!response.ok)
    throw new Error(`Failed to open late daily game session for ${date}`);
  return response.json();
};

export const openSurvivalGameSession = async (): Promise<GameSessionData> => {
  const apiUrl = getApiUrl();

  const response = await fetch(`${apiUrl}/sessions/survival`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!response.ok) throw new Error(`Failed to open survival game session`);
  return response.json();
};

export const openArcadeGameSession = async (
  data: ArcadeConfigData
): Promise<GameSessionData> => {
  const apiUrl = getApiUrl();

  const response = await fetch(`${apiUrl}/sessions/arcade`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(data),
    credentials: 'include'
  });

  if (!response.ok) {
    await throwApiError(response, 'Failed to open arcade game session');
  }

  return response.json();
};

export class SeedNotFoundError extends Error {
  constructor() {
    super('Seed not found');
    this.name = 'SeedNotFoundError';
  }
}

/**
 * Open a Spotlight game session.
 *
 * @param spotlightId - Spotlight to start a session for
 * @param seed - Optional Seed: the id of another player's completed session
 * to reuse the exact same riddles, in the exact same order
 *
 * @throws SeedNotFoundError when the given Seed does not resolve to a
 * completed session for this Spotlight (404 response)
 */
export const openSpotlightGameSession = async (
  spotlightId: string,
  seed?: string
): Promise<GameSessionData> => {
  const apiUrl = getApiUrl();
  const query = seed ? `?seed=${encodeURIComponent(seed)}` : '';

  const response = await fetch(
    `${apiUrl}/sessions/spotlight/${spotlightId}${query}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Anonymous-ID': getOrCreateAnonymousId()
      },
      credentials: 'include'
    }
  );

  if (response.status === 404 && seed) throw new SeedNotFoundError();
  if (!response.ok) throw new Error(`Failed to open spotlight game session`);

  return response.json();
};
